import React from 'react'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/SearchRounded'
import AttachmentIcon from '@mui/icons-material/AttachFileRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import type {
  ListMyPrepItemsParams,
  MyPrepListItemsResponse,
  PrepListItem,
  PrepListResponse,
} from '../../api/prep'
import type { RequestOptions } from '../../api'

const PREP_PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

type PrepPickerApi = {
  listMyPrepItems: (
    params: ListMyPrepItemsParams & { ids_only?: false },
    options?: RequestOptions,
  ) => Promise<MyPrepListItemsResponse>
  searchPrepItems: (params: { scope?: 'my' | 'station' | 'all' | 'global'; q?: string; limit?: number; cursor?: string | null }, options?: RequestOptions) => Promise<PrepListResponse>
}

export interface PrepPickerProps {
  open: boolean
  onClose: () => void
  stationId: number | null
  onAttach: (prepItemId: number) => Promise<void>
  api: PrepPickerApi
  attachedItemIds?: number[]
  onViewPrep?: (id: number, invoker?: HTMLElement | null) => void
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? fallback)
  }
  return fallback
}

const dedupeAppend = (existing: PrepListItem[], incoming: PrepListItem[]): PrepListItem[] => {
  if (incoming.length === 0) {
    return existing
  }
  const existingIds = new Set(existing.map((item) => item.id))
  const filtered = incoming.filter((item) => !existingIds.has(item.id))
  return filtered.length > 0 ? [...existing, ...filtered] : existing
}

const PrepPicker: React.FC<PrepPickerProps> = ({
  open,
  onClose,
  stationId,
  onAttach,
  api,
  attachedItemIds = [],
  onViewPrep,
}) => {
  const [query, setQuery] = React.useState('')
  const [debouncedQuery, setDebouncedQuery] = React.useState('')
  const [items, setItems] = React.useState<PrepListItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [hasMore, setHasMore] = React.useState(false)
  const [myPage, setMyPage] = React.useState(1)
  const [searchCursor, setSearchCursor] = React.useState<string | null>(null)
  const [mode, setMode] = React.useState<'my' | 'search'>('my')
  const [attachingId, setAttachingId] = React.useState<number | null>(null)
  const searchTimerRef = React.useRef<number | null>(null)
  const requestControllerRef = React.useRef<AbortController | null>(null)

  const attachedIdsSet = React.useMemo(() => new Set(attachedItemIds), [attachedItemIds])

  const resetState = React.useCallback(() => {
    setQuery('')
    setDebouncedQuery('')
    setItems([])
    setLoading(false)
    setLoadingMore(false)
    setError(null)
    setHasMore(false)
    setMyPage(1)
    setSearchCursor(null)
    setMode('my')
    setAttachingId(null)
  }, [])

  const abortInFlight = React.useCallback(() => {
    if (requestControllerRef.current) {
      requestControllerRef.current.abort()
      requestControllerRef.current = null
    }
  }, [])

  React.useEffect(() => {
    return () => {
      abortInFlight()
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
    }
  }, [abortInFlight])

  React.useEffect(() => {
    if (!open) {
      abortInFlight()
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
      resetState()
      return
    }

    if (stationId === null) {
      setError('Select a station to attach prep items.')
      setItems([])
      return
    }

    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
    }
  }, [open, query, resetState, abortInFlight, stationId])

  const fetchMyPrep = React.useCallback(
    async (page: number, append: boolean) => {
      if (stationId === null) {
        setItems([])
        setError('Select a station to attach prep items.')
        return
      }

      abortInFlight()
      const controller = new AbortController()
      requestControllerRef.current = controller

      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setItems([])
      }
      setError(null)

      try {
        const response = await api.listMyPrepItems(
          { page, limit: PREP_PAGE_SIZE, ids_only: false },
          { signal: controller.signal },
        )
        setMode('my')
        setItems((prev) => (append ? dedupeAppend(prev, response.items) : response.items))
        const currentPage = typeof response.page === 'number' && Number.isFinite(response.page) ? response.page : page
        const perPageCandidate = (response as MyPrepListItemsResponse).limit ??
          (response as MyPrepListItemsResponse).per_page ??
          (response as MyPrepListItemsResponse & { perPage?: number }).perPage ??
          PREP_PAGE_SIZE
        const perPage = typeof perPageCandidate === 'number' && Number.isFinite(perPageCandidate)
          ? Math.max(1, Math.floor(perPageCandidate))
          : PREP_PAGE_SIZE
        const totalCandidate = typeof response.total === 'number' && Number.isFinite(response.total)
          ? response.total
          : (response.count ?? 0)
        setMyPage(currentPage)
        setHasMore(currentPage * perPage < totalCandidate)
        setSearchCursor(null)
      } catch (fetchError) {
        if ((fetchError as DOMException)?.name === 'AbortError') {
          return
        }
        setError(getErrorMessage(fetchError, 'Unable to load your prep items.'))
      } finally {
        if (requestControllerRef.current === controller) {
          requestControllerRef.current = null
        }
        if (append) {
          setLoadingMore(false)
        } else {
          setLoading(false)
        }
      }
    },
    [abortInFlight, api, stationId],
  )

  const fetchSearch = React.useCallback(
    async (cursor: string | null, append: boolean) => {
      const trimmed = debouncedQuery.trim()
      if (!trimmed) {
        return
      }

      abortInFlight()
      const controller = new AbortController()
      requestControllerRef.current = controller

      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setItems([])
      }
      setError(null)

      try {
        const response = await api.searchPrepItems(
          {
            scope: 'all',
            q: trimmed,
            limit: PREP_PAGE_SIZE,
            cursor: append ? cursor ?? undefined : undefined,
          },
          { signal: controller.signal },
        )
        setMode('search')
        setItems((prev) => (append ? dedupeAppend(prev, response.items) : response.items))
        setHasMore(Boolean(response.next_cursor))
        setSearchCursor(response.next_cursor ?? null)
      } catch (fetchError) {
        if ((fetchError as DOMException)?.name === 'AbortError') {
          return
        }
        setError(getErrorMessage(fetchError, 'Unable to search prep items.'))
      } finally {
        if (requestControllerRef.current === controller) {
          requestControllerRef.current = null
        }
        if (append) {
          setLoadingMore(false)
        } else {
          setLoading(false)
        }
      }
    },
    [abortInFlight, api, debouncedQuery],
  )

  React.useEffect(() => {
    if (!open) {
      return
    }

    if (stationId === null) {
      setItems([])
      setError('Select a station to attach prep items.')
      return
    }

    if (debouncedQuery) {
      void fetchSearch(null, false)
    } else {
      void fetchMyPrep(1, false)
    }
  }, [open, stationId, debouncedQuery, fetchMyPrep, fetchSearch])

  const handleAttach = async (item: PrepListItem) => {
    if (attachedIdsSet.has(item.id)) {
      return
    }
    setAttachingId(item.id)
    try {
      await onAttach(item.id)
      setItems((prev) => prev.filter((entry) => entry.id !== item.id))
    } catch (attachError) {
      setError(getErrorMessage(attachError, 'Unable to attach prep item.'))
    } finally {
      setAttachingId((current) => (current === item.id ? null : current))
    }
  }

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) {
      return
    }
    if (mode === 'my') {
      void fetchMyPrep(myPage + 1, true)
    } else {
      void fetchSearch(searchCursor, true)
    }
  }

  const emptyMessage = React.useMemo(() => {
    if (loading || error) {
      return null
    }
    if (mode === 'search' && debouncedQuery) {
      return `No results for "${debouncedQuery}".`
    }
    return 'No prep items found in My Prep.'
  }, [debouncedQuery, error, loading, mode])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Prep Item</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search prep"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            fullWidth
            autoFocus
          />
          {loading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Stack spacing={1.5}>
            {items.map((item) => {
              const alreadyAttached = attachedIdsSet.has(item.id)
              const isAttaching = attachingId === item.id
              return (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {item.headline}
                      </Typography>
                      {item.tease ? (
                        <Typography variant="body2" color="text.secondary">
                          {item.tease}
                        </Typography>
                      ) : null}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="View prep details">
                        <span>
                          <IconButton
                            aria-label="View prep details"
                            onClick={(event) => onViewPrep?.(item.id, event.currentTarget)}
                            size="small"
                            disabled={!onViewPrep}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        variant={alreadyAttached ? 'outlined' : 'contained'}
                        size="small"
                        startIcon={
                          alreadyAttached ? undefined : isAttaching ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <AttachmentIcon fontSize="small" />
                          )
                        }
                        onClick={alreadyAttached ? undefined : () => handleAttach(item)}
                        disabled={alreadyAttached || isAttaching}
                      >
                        {alreadyAttached ? 'Attached' : isAttaching ? 'Adding…' : 'Add'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
          {!loading && !error && items.length === 0 && emptyMessage ? (
            <Typography color="text.secondary" align="center">
              {emptyMessage}
            </Typography>
          ) : null}
          {hasMore ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button onClick={handleLoadMore} disabled={loadingMore || loading} variant="outlined">
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PrepPicker
